import React, { FC, useCallback, useEffect, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { Box, InputAdornment, Typography, TextField } from '@mui/material'
import { GridApiRef, GridColDef, useGridApiRef } from '@mui/x-data-grid-pro'
import BasicDataGrid from '../../generic/datagrid/index'
import CheckboxCell from '../../generic/datagrid/cells/CheckboxCell'
import { isDefaultRow } from './utils/helperFunctions'
import { GS_ROLE_INFO } from 'utils/constants'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore.ts'
import SettingsList from 'components/layouts/settings/SettingsList.tsx'
import { useTheme } from '@mui/styles'
import InputFormat from 'components/generic/inputs/formatting/InputFormat'

const boxStyles = {
  marginTop: '1rem',
  width: '100%',
  maxWidth: '500px',
  '& .MuiDataGrid-columnHeaderTitleContainer': {
    padding: 0,
  },
  '& .MuiDataGrid-columnHeader': {
    px: 1,
  },
  '& .MuiDataGrid-cell': {
    px: 1,
  },
  '& .MuiDataGrid-columnSeparator': {
    visibility: 'hidden',
  },
}

/*
  This is the data for the legacy settings list component.
  It is used when the feature flag for role based payment thresholds is disabled.
 */
const legacySettingListComponentData = [
  {
    componentType: 'checkbox',
    label: 'Set a payment balance threshold',
    path: 'purchaseSettings.payment.highBalancePaymentAuthorization.hasThreshold',
  },
  {
    componentType: 'input',
    label: 'Ticket Value Threshold',
    path: 'purchaseSettings.payment.highBalancePaymentAuthorization.ticketValueThreshold',
    inputType: 'price',
    disabledBy: [
      {
        key: 'purchaseSettings.payment.highBalancePaymentAuthorization.hasThreshold',
        value: false,
      },
    ],
  },
]

interface SettingsPaymentAuthorizationProps {
  title: string
  subTitle: string
  subTitle2?: string
  path: string
  dataGridProps?: any
  setHasEditedGrid: (value: boolean) => void
  apiRefDict: Record<string, GridApiRef>
  setApiRefDict: React.Dispatch<React.SetStateAction<Record<string, GridApiRef>>>
}

const SettingsPaymentAuthorization: FC<SettingsPaymentAuthorizationProps> = ({
  title,
  subTitle,
  path,
  dataGridProps,
  setHasEditedGrid,
  apiRefDict,
  setApiRefDict,
}) => {
  const apiRef = useGridApiRef()
  const methods = useFormContext()
  const theme = useTheme()

  const roleBasedThresholds = methods.getValues(path)

  function highBalancePaymentColumns(): Array<GridColDef> {
    return [
      {
        field: 'id',
        headerName: 'Role',
        flex: 1,
        headerAlign: 'left',
        align: 'left',
        renderCell: params => {
          const { row } = params
          const textColor = row.enabled ? theme.palette.text.primary : theme.palette.text.disabled
          return <Typography sx={{ color: textColor }}>{GS_ROLE_INFO[row.id]?.name || row.id}</Typography>
        },
      },
      {
        field: 'enabled',
        headerName: 'Enabled',
        renderCell: params => <CheckboxCell {...params} disabled={isDefaultRow(params)} />,
        headerAlign: 'center',
        align: 'center',
        sortable: false,
        flex: 0.5,
      },
      {
        field: 'threshold',
        headerName: 'Payment Threshold',
        flex: 1,
        sortable: false,
        renderCell: params => {
          const { row } = params
          const textColor = row.enabled && row.threshold ? theme.palette.text.primary : theme.palette.text.disabled
          const placeholder = row.enabled ? 'Enter an amount' : 'Not set'
          const startAdornment = row.threshold ? (
            <InputAdornment position="end">
              <Typography sx={{ color: textColor }}>$</Typography>
            </InputAdornment>
          ) : null

          return (
            <TextField
              sx={{ width: '100%' }}
              label=""
              data-test={`${row?.id}-threshold`}
              value={row?.threshold}
              placeholder={placeholder}
              variant="standard"
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const { value } = event.target
                apiRef.current?.updateRows([{ ...row, threshold: value }])
              }}
              InputProps={{
                inputComponent: InputFormat,
                disableUnderline: true,
                startAdornment: startAdornment,
                disabled: !row.enabled, // Disable input when checkbox is not checked
                style: {
                  color: textColor,
                },
              }}
            />
          )
        },
      },
    ]
  }

  // Without useMemo, each re-render of the component might cause dataRows to be recalculated unnecessarily,
  // potentially causing the first edit to be "lost" because the component would re-render before the edit was fully committed.
  const dataRows = useMemo(() => {
    return roleBasedThresholds?.map(row => {
      return {
        id: row.role,
        ...row,
      }
    })
  }, [roleBasedThresholds])

  /**
   * when grid updated also update the form value(s)
   */
  const onUpdateGrid = useCallback(() => {
    setHasEditedGrid(true)
    const newRows = Array.from(apiRef.current.getRowModels().values())
    methods.setValue(path, newRows)
  }, [apiRef])

  useEffect(() => {
    if (apiRef?.current?.subscribeEvent) {
      apiRef.current.subscribeEvent('rowsUpdate', () => {
        onUpdateGrid()
      })
    }
    if (!apiRefDict[path]) {
      setApiRefDict(prev => ({
        ...prev,
        [path]: apiRef,
      }))
    }
  }, [apiRef])

  return (
    <>
      <Typography variant="h6" color="textPrimary">
        {title}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        {subTitle}
      </Typography>

      <Box sx={boxStyles}>
        <BasicDataGrid
          density="compact"
          height="auto"
          rows={dataRows}
          columns={highBalancePaymentColumns()}
          apiRef={apiRef}
          editMode="cell"
          disableColumnMenu
          hideFooter
          onCellClick={(_, event) => {
            event.stopPropagation()
            Object.values(apiRefDict).forEach(ref => {
              ref.current.state.focus.cell = null
            })
          }}
          {...dataGridProps}
        />
      </Box>
    </>
  )
}

export default SettingsPaymentAuthorization
